import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosInstance } from 'axios';
import { jotaiStore } from 'init/jotai';
import { i18nKeys, translationStateAtom } from 'locales';
import { NotificationTypesSchema } from 'types/notification-types';
import { z } from 'zod/v4';

import { type ApiResponse, addActionNameToMutationResult, useLegacyAxiosInstance } from './utils';

const { t } = jotaiStore.get(translationStateAtom);

const nullableString = z.string().nullable();

export const ContactPersonFormSchema = z.object({
  first_name: z.string().min(2, { error: t(i18nKeys.ERROR.FIELD_REQUIRED) }),
  last_name: z.string().min(2, { error: t(i18nKeys.ERROR.FIELD_REQUIRED) }),
  job_title: z.string().nullable().optional(),
  main_contact: z.boolean().nullable().optional(),
  locale: z.string().nullable().optional(),
  use_postal_address: z.boolean().nullable().optional(),
  address: z
    .object({
      street_name: nullableString,
      street_number: nullableString,
      street_box: nullableString,
      zip_code: nullableString,
      city: nullableString,
      country_code: nullableString,
      additional_line_1: nullableString,
      additional_line_2: nullableString,
      country: nullableString,
    })
    .nullable(),
  emails: z
    .array(
      z.string().refine(val => val === '' || z.email().safeParse(val).success, {
        error: t(i18nKeys.ERROR.INVALID_EMAIL),
      }),
    )
    .min(1, { error: t(i18nKeys.ERROR.FIELD_REQUIRED) })
    .refine(emails => emails.some(email => email.trim() !== ''), {
      error: t(i18nKeys.ERROR.FIELD_REQUIRED),
    }),
  phones: z
    .array(
      z.object({
        id: z.string().optional(),
        number: z.string().refine(val => val.length >= 10 || val.length === 0, {
          error: t(i18nKeys.ERROR.INVALID_PHONE),
        }),
        _destroy: z.boolean().optional(),
      }),
    )
    .optional(),
  notifications: z.array(NotificationTypesSchema).optional(),
});

const ContactPersonFormSchemaSnakeCase = ContactPersonFormSchema.transform(data => {
  const { address, ...rest } = data;

  return {
    ...rest,
    address: address ? { attributes: address } : null,
  };
});

export type CreateContactPersonForm = z.infer<typeof ContactPersonFormSchema>;

type CreateContactPersonVariables = {
  debtorId: string;
  data: CreateContactPersonForm;
};

export async function createContactPersonFn(
  axiosInstance: Promise<AxiosInstance>,
  { debtorId, data }: CreateContactPersonVariables,
) {
  const instance = await axiosInstance;

  const snakeCaseData = ContactPersonFormSchemaSnakeCase.parse(data);
  return instance.post(`/debtors/${debtorId}/contact_persons`, snakeCaseData);
}

export function useCreateContactPerson() {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    CreateContactPersonVariables
  >({
    mutationFn: variables => createContactPersonFn(axiosInstance, variables),
    onSuccess: () => client.invalidateQueries({ queryKey: ['contact-persons'] }),
  });

  return addActionNameToMutationResult<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    CreateContactPersonVariables,
    'createContactPerson'
  >('createContactPerson', mutation);
}
